/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { FC, useState } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useMutation, useQuery } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Paper,
  Grid,
  Container,
  Button,
  TextField,
  NativeSelect,
  FormHelperText,
  FormControl,
} from '@material-ui/core';
import { useStyles } from './styles';
import { UserFormInputPattern, ViewEndUserData, GenericFormProps } from './types';
import { observer } from 'mobx-react-lite';
import { handleCompanyUrl } from 'utils/organizationUtils';

export const ViewEndUserForm: FC<GenericFormProps> = observer(({ selfRegister }) => {
  const classes = useStyles();
  const history = useHistory();
  const [status, setStatus] = useState('accepted');
  const params = useParams<{ id: string }>();
  const {
    ldapStore: { getProfileInfo, acceptEndUserRegisteration, profileInfo },
    notificationStore: { openNotification },
    appPreloaderStore: { disablePreloader },
  } = useStore();

  const createUserReq = useMutation((data: ViewEndUserData) => acceptEndUserRegisteration(Number(params.id), data), {
    mutationKey: 'createUserReq',
    onSuccess: (e, b) => {
      disablePreloader();
      openNotification('success', `${b.status}`);
      history.push(handleCompanyUrl('/dashboard'));
    },
    onError: ({ message }: Error) => openNotification('error', message),
  });

  const getUserProfileinfo = useQuery('getUserProfileInfo', () => getProfileInfo(Number(params.id)), {
    onError: (e) => {
      openNotification('error', `Can't get enduser info`);
    },
  });

  useAppPreloader(getUserProfileinfo.isFetching || createUserReq.isLoading);
  const { control, handleSubmit } = useForm<ViewEndUserData>();
  const onSubmit = (data: ViewEndUserData) => {
    const sentData = { ...data, status: status };
    return createUserReq.mutate(sentData);
  };

  const mockDataPers1: UserFormInputPattern[] = [
    { label: 'First name', key: 'givenname', required: true, autoFocus: true },
    { label: 'Middle name', key: 'middleName', required: false },
    { label: 'Last name', key: 'sn', required: true },
    {
      label: 'Login',
      key: 'uid',
      required: true,
      pattern: {
        regexp: /^[0-9a-z_]+$/,
        errorMessage: 'This field must contain only digits, lowercase letters or underscores',
      },
    },
    { label: 'Email', key: 'mail', required: true },
    { label: 'Phone', key: 'phone', required: false },
    { label: 'Department', key: 'department', required: false },
    { label: 'Role', key: 'role', required: false },
    { label: 'Group', key: 'group', required: false },
  ];

  return !getUserProfileinfo.isFetching ? (
    <form onSubmit={handleSubmit(onSubmit)} className={classes.box}>
      <Grid container justify='space-between'>
        <Paper className={classes.paper}>
          <p className={classes.boxTitle}>General Information</p>
          <Grid container>
            <Container className={classes.tableBox}>
              {mockDataPers1.map((item, index) => (
                <Controller
                  key={item.key}
                  name={item.key}
                  control={control}
                  rules={{ required: item?.required }}
                  defaultValue={profileInfo?.[item.key]}
                  render={({ field, fieldState: { invalid, error } }) => (
                    <Grid key={index} container className={item?.multiline ? classes.rowWide : classes.row}>
                      <p className={classes.title}>{item.label}</p>
                      {item.type === 'select' ? (
                        <FormControl className={invalid ? classes.inputError : classes.input}>
                          <NativeSelect {...field} error={invalid} fullWidth autoFocus={item?.autoFocus}>
                            {item?.options
                              ? item.options.map((item) => (
                                  <option key={item.value} value={item.value}>
                                    {item.label}
                                  </option>
                                ))
                              : null}
                          </NativeSelect>
                          {invalid ? <FormHelperText error>Required field</FormHelperText> : null}
                        </FormControl>
                      ) : (
                        <TextField
                          {...field}
                          autoFocus={item?.autoFocus}
                          className={invalid ? classes.inputError : classes.input}
                          multiline={item?.multiline}
                          rows={item?.multiline ? 4 : 1}
                          placeholder={item.placeholder}
                          type={item.type ? item.type : 'text'}
                          error={invalid}
                          helperText={
                            invalid ? (error?.type === 'pattern' ? item.pattern?.errorMessage : 'Required field') : ''
                          }
                        />
                      )}
                    </Grid>
                  )}
                />
              ))}
            </Container>
          </Grid>
        </Paper>
      </Grid>
      <div className={classes.btnBox}>
        <Button
          type='submit'
          onClick={() => {
            setStatus('accepted');
          }}
          variant='contained'
          color='secondary'
          className={classes.btnWide}
        >
          Accept
        </Button>
        <Button
          type='submit'
          onClick={() => {
            setStatus('rejected');
          }}
          variant='contained'
          color='default'
          className={classes.btnWide}
        >
          Reject
        </Button>
      </div>
    </form>
  ) : null;
});
